import React from "react";
import { Container, Alert } from "@mui/material";
import { useParams, Navigate } from "react-router-dom";
import { useCompanyEarningsChart } from "./hooks/useCompanyEarnings";
import { useCompanyProfile } from "./hooks/useCompanyProfile";
import ViewCompanyEarningsWrapper from "./ViewCompanyEarningsWrapper";
import { ViewCompanyEarningsSkeleton } from "./ViewCompanyEarningsSkeleton";
import ErrorDisplay from "../../common/ErrorDisplay";
import { StockCurrencyProvider } from "../../../contexts/StockCurrencyContext";
import { useMembership, FREE_TIER_TICKER } from "../../../contexts/MembershipContext";

const ViewCompanyEarningsLoader: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const { isMembershipActive } = useMembership();

  const {
    chartData,
    rawData,
    isLoading,
    error,
    symbol,
  } = useCompanyEarningsChart(ticker ?? "");

  // Fetch profile to get currency info for the StockCurrencyProvider
  const {
    data: profileData,
    isLoading: isProfileLoading,
  } = useCompanyProfile(ticker ?? "");

  // Extract profile from array or single object response
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;

  // Ticker guard for free tier users
  if (!isMembershipActive && ticker?.toUpperCase() !== FREE_TIER_TICKER) {
    return <Navigate to="/earnings" replace />;
  }

  // Show skeleton while loading
  if (isLoading || isProfileLoading) {
    return <ViewCompanyEarningsSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorDisplay 
          error={error} 
          onRetry={() => window.location.reload()} 
          variant="card"
          fullWidth 
        />
      </Container>
    );
  }

  // Show error if no ticker provided
  if (!ticker) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning">
          No ticker symbol provided in the URL
        </Alert>
      </Container>
    );
  }

  // Show error if no data found
  if (!rawData || !chartData || chartData.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="info">
          No earnings data found for ticker: {ticker}
        </Alert>
      </Container>
    );
  }

  // Render the actual ViewCompanyEarningsWrapper component with data
  // Wrap with StockCurrencyProvider so all child components can access currency formatting
  return (
    <StockCurrencyProvider currency={profile?.currency} exchange={profile?.exchange}>
      <ViewCompanyEarningsWrapper
        ticker={ticker}
        symbol={symbol || ticker}
        chartData={chartData}
      />
    </StockCurrencyProvider>
  );
};

export default ViewCompanyEarningsLoader;